<template>
  <div class="column content-box settings--content">
    <p class="knowledge-base-alert">
      {{ $t('INTEGRATION_SETTINGS.ASSISTENT.PUBLIC_KNOWLEDGE_BASE.ALERT') }}
    </p>

    <div class="search-wrap">
      <fluent-icon icon="search" class="search-icon" />
      <input
        v-model="searchQuery"
        type="text"
        class="content-search"
        data-testid="public-knowledge-base-search"
        :placeholder="
          $t(
            'INTEGRATION_SETTINGS.ASSISTENT.PUBLIC_KNOWLEDGE_BASE.SEARCH_PLACEHOLDER'
          )
        "
      />
    </div>

    <woot-loading-state v-if="uiFlags.isFetching" />

    <p v-else-if="hasFetchError" class="no-items-error-message">
      {{ $t('INTEGRATION_SETTINGS.ASSISTENT.PUBLIC_KNOWLEDGE_BASE.FETCH_ERROR') }}
    </p>

    <p v-else-if="!filteredBases.length" class="no-items-error-message">
      {{ $t('INTEGRATION_SETTINGS.ASSISTENT.PUBLIC_KNOWLEDGE_BASE.NOT_FOUND') }}
    </p>

    <div v-else class="public-knowledge-bases-grid">
      <public-knowledge-base-card
        v-for="base in filteredBases"
        :key="base.id"
        :name="base.name"
        :description="base.description"
        :avatar-url="base.avatar_url"
        :is-enabled="isEnabled(base.id)"
        :is-loading="isSaving && pendingBaseId === base.id"
        :is-busy="isSaving"
        @toggle="onToggle(base)"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import assistentTabMixin from '../../mixins/assistentTabMixin';
import PublicKnowledgeBaseCard from '../PublicKnowledgeBaseCard';
import {
  sortPublicKnowledgeBases,
  filterPublicKnowledgeBasesByName,
} from '../../helpers/publicKnowledgeBases';
import { toBCP47Locale } from 'dashboard/helper/menuItemVisibility';

const enabledIdsOf = assistent =>
  (assistent.enabled_public_knowledge_bases || []).map(base => base.id);

export default {
  components: {
    PublicKnowledgeBaseCard,
  },
  mixins: [assistentTabMixin],
  data() {
    return {
      searchQuery: '',
      pendingBaseId: null,
      initialEnabledIds: enabledIdsOf(this.assistent),
      hasFetchError: false,
    };
  },
  computed: {
    ...mapGetters({
      publicKnowledgeBases: 'publicKnowledgeBases/getPublicKnowledgeBases',
      uiFlags: 'publicKnowledgeBases/getUIFlags',
    }),
    enabledIds() {
      return enabledIdsOf(this.assistent);
    },
    sortedBases() {
      return sortPublicKnowledgeBases(
        this.publicKnowledgeBases,
        this.initialEnabledIds,
        toBCP47Locale(this.$i18n.locale)
      );
    },
    filteredBases() {
      return filterPublicKnowledgeBasesByName(
        this.sortedBases,
        this.searchQuery
      );
    },
  },
  mounted() {
    this.fetchPublicKnowledgeBases();
  },
  methods: {
    async fetchPublicKnowledgeBases() {
      try {
        await this.$store.dispatch('publicKnowledgeBases/get');
      } catch (error) {
        this.hasFetchError = true;
        this.showAlert(
          this.$t(
            'INTEGRATION_SETTINGS.ASSISTENT.PUBLIC_KNOWLEDGE_BASE.FETCH_ERROR'
          )
        );
      }
    },
    isEnabled(id) {
      return this.enabledIds.includes(id);
    },
    async onToggle(base) {
      if (this.isSaving) return;

      const nextIds = this.isEnabled(base.id)
        ? this.enabledIds.filter(id => id !== base.id)
        : [...this.enabledIds, base.id];

      this.pendingBaseId = base.id;
      await this.saveAssistent({ public_knowledge_base_ids: nextIds });
      this.pendingBaseId = null;
    },
  },
};
</script>

<style scoped lang="scss">
@import './tabs-shared.scss';

.search-wrap {
  align-items: center;
  display: flex;
  margin-bottom: var(--space-normal);
  max-width: 400px;
  min-width: 150px;
  position: relative;

  .search-icon {
    color: var(--b-700);
    left: var(--space-one);
    position: absolute;
  }

  .content-search {
    border-color: var(--s-100);
    height: 3.8rem;
    margin: 0;
    padding-left: var(--space-large);
    width: 100%;
  }
}

.public-knowledge-bases-grid {
  display: grid;
  align-items: stretch;
  gap: var(--space-normal);
  grid-auto-rows: 1fr;
  grid-template-columns: 1fr;

  @media screen and (min-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media screen and (min-width: 1024px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media screen and (min-width: 1440px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}
</style>
